﻿angular.module('projectModule')
    .service('ntaAlert',
        ['$log', '$mdDialog', 'progressCircle',
function ($log,   $mdDialog,   progressCircle) {
    'use strict';
    const self = this;

    /// ntaAlert bevat meerdere alerts, en voorkomt dat een alert van hetzelfde type meerdere keren
    ///  achtereen weergegeven wordt.

    /// -- Imports --------------------------------------------------------------------------------

    // (geen imports)


    /// -- Instance variables ---------------------------------------------------------------------

    const _warnings = new Map();
    const _errors = new Map();

    const _promises = new Map();
    const _alerts = new Map();


    /// -- Exports --------------------------------------------------------------------------------

    Object.assign(self, {
        setWarningsAndErrors,

        showWarning,
        showError,
        showNoEdit,
        showMaintenance,
        showLogin,
        showNetworkError,
    });


    /// -- Initialization -------------------------------------------------------------------------

    // (geen initialisatie nodig)


    /// -- Implementation -------------------------------------------------------------------------

    function setWarningsAndErrors(warnings, errors) {
        for (const warning of warnings ?? []) {
            _warnings.set(warning.Id, warning);
        }
        for (const error of errors ?? []) {
            _errors.set(error.Id, error);
        }
    } //-- end: setWarningsAndErrors --------------------------------------------------------------

    function showWarning(code, args, argsAreHtml = false) {
        // De parameter ‘args’ bevat waarden voor de parameters in de melding (alles wat in de
        //  Value van de warning {{tussen accolades}} staat), en kan het volgende zijn:
        // - een Map met alle argumenten in de melding: args.set('tussen accolades', value)
        // - een object: { 'tussen accolades': value }
        // - een array van objecten: [{ from: 'tussen accolades', to: value }, ... ]
        // - leeg; dan vindt er geen vervanging plaats.
        if (args) {
            if (args instanceof Map) {
                // dan ongemoeid laten
            } else if (Array.isArray(args)) {
                args = new Map(args.map(arg => [arg.from, arg.to]));
            } else if (typeof args === 'object') {
                args = new Map(Object.entries(args));
            } else {
                $log.error('ntaAlert.showWarning: args should be an array, object, or Map (using empty Map instead):', args);
                args = new Map();
            }
        } else {
            args = new Map();
        }

        // de parameter argsAreHtml kan het volgende zijn:
        // - een function: deze krijgt de naam van het argument, en geeft terug of dit HTML is
        //   of niet
        // - een Set: deze bevat de namen van alle argumenten die HTML bevatten
        // - een Map: deze geeft voor elke argumentnaam aan of deze HTML bevat of niet.
        // - elk ander datatype wordt beoordeeld als een Boolean die aangeeft of alle argumenten
        //   HTML bevatten (true), of dat ze geen van alle HTML bevatten (false).
        const isArgHtml = typeof argsAreHtml === 'function' ? argsAreHtml
            : argsAreHtml instanceof Set ? name => argsAreHtml.has(name)
            : argsAreHtml instanceof Map ? name => argsAreHtml.get(name)
            : () => argsAreHtml;

        const warning = _warnings.get(code)
            ?? _errors.get(code)
            ?? { Id: code, FilterValue1: code, Value: code };

        // FilterValue1 = de titel
        // FilterValue2 = de tekst van de ‘OK’ button
        // FilterValue3 = de tekst van de ‘cancel’ button
        // (Als FilterValue3 niet opgegeven is, wordt `alert` gebruikt, anders `confirm`).

        const title = warning.FilterValue1 || 'Waarschuwing';
        const htmlContent = warning.Value
            .replaceAll(/{{?(.*?)}}?/g, (match, name) => {
                name = name.trim();
                if (args.has(name)) {
                    const value = args.get(name);
                    return isArgHtml(name) ? value : htmlEncode(value);
                } else {
                    return match;
                }
            });

        const key = 'warning:' + code
            + (args.size
                ? '|' + Array.from(args.entries())
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([k, v]) => `${k}=${v}`)
                    .join('|')
                : '');
        return showAlert(key,
            () => {
                const alert = warning.FilterValue3
                    ? $mdDialog.confirm()
                    : $mdDialog.alert();
                alert.title(title);
                alert.htmlContent(htmlContent);
                alert.ok(warning.FilterValue2 || 'OK');
                if (warning.FilterValue3) {
                    alert.cancel(warning.FilterValue3);
                }
                return alert;
            },
            () => args.size && _alerts.delete(key) // alerts met argumenten meteen verwijderen uit de cache
        );
    } //-- end: showWarning -----------------------------------------------------------------------

    function showError() {
        const oepsCode = '[W155]'; // Er is iets misgegaan. Kunt u niet verder door dit probleem? Neem dan contact op met de helpdesk. Excuses voor het ongemak.
        if (_warnings.has(oepsCode)) {
            return showWarning(oepsCode);
        } else {
            return showAlert('error',
                () => $mdDialog.alert()
                    .title('Oeps...')
                    .htmlContent(`
                            <div>Er is iets niet goed gegaan.</div>
                            <div>Kunt u niet verder door dit probleem? Neem dan contact op met onze helpdesk.</div>
                            <div>&nbsp;</div>
                            <div>Excuses voor het ongemak.</div>
                        `)
                    .ok('OK'),
            );
        }
    } //-- end: showError -------------------------------------------------------------------------

    function showNoEdit() {
        return showAlert('noEdit', () => $mdDialog.alert()
            .title('Berekening is vergrendeld')
            .htmlContent(`
                <div>De berekening is vergrendeld of afgemeld.</div>
                <div>Wijzigingen worden niet opgeslagen.</div>
            `)
            .ok('OK'));
    } //-- end: showNoEdit ------------------------------------------------------------------------

    function showMaintenance() {
        return showAlert('maintenance', () => $mdDialog.alert()
            .title('Onderhoud')
            .htmlContent(`
                <div>Er wordt momenteel onderhoud gepleegd aan Uniec 3. Wacht 15 à 30 minuten alvorens op OK te klikken.</div>
                <div>&nbsp;</div>
                <div>Kunt u niet verder door dit probleem? Neem dan contact op met onze helpdesk.</div>
                <div>&nbsp;</div>
                <div>Excuses voor het ongemak.</div>`)
            .ok('OK'));
    } //-- end: showMaintenance -------------------------------------------------------------------

    function showLogin(url = location.href) {
        return showAlert('login',
            () => $mdDialog.alert()
                .title('U bent uitgelogd')
                .htmlContent(`
                        <div>Om verder te werken moet u opnieuw inloggen.</div>
                        <div>Klik op OK om door te gaan naar de inlogpagina.</div>
                    `)
                .ok('OK'),
            () => {
                if (url) {
                    window.location = '/Account/Login?returnUrl=' + encodeURIComponent(url);
                } else {
                    location.reload();
                }
            });
    } //-- end: showLogin -------------------------------------------------------------------------

    function showNetworkError(url) {
        return showAlert('networkError',
            () => $mdDialog.alert()
                .title('Verbindingsprobleem')
                .htmlContent(`
                        <div>Uniec 3 kan de server niet bereiken. Controleer uw internetverbinding.</div>
                    `)
                .ok('Opnieuw proberen'),
            () => {
                if (url) {
                    window.location = url;
                } else {
                    location.reload();
                }
            }
        );
    } //-- end: showNetworkError ------------------------------------------------------------------

    function showAlert(name, createAlert, onComplete = null) {
        // Als deze alert al getoond wordt, dan doen we niets.
        let promise = _promises.get(name);
        if (!promise) {
            // Anders halen we sowieso de progressCircle weg, zodat deze de alert niet in de weg zit.
            progressCircle.setShowProgressValue(false);

            // We halen de alert uit de cache, en anders maken we een nieuwe aan.
            let alert = _alerts.get(name);
            if (!alert) {
                alert = createAlert();
                _alerts.set(name, alert);
            }

            // Dan tonen we de alert en slaan de promise op in de cache.
            promise = $mdDialog.show(alert);
            _promises.set(name, promise);

            // We zorgen ervoor dat een evt. onComplete callback t.z.t. wordt aangeroepen.
            if (typeof onComplete === 'function') {
                promise.then(onComplete).catch(onComplete);
            }

            // En we zorgen ervoor dat de promise wordt verwijderd uit de cache zodra deze is afgerond.
            promise.finally(() => _promises.delete(name));
        }
        return promise;
    } //-- end: showAlert -------------------------------------------------------------------------

    function htmlEncode(text) {
        if (!text) return text;

        const span = document.createElement('span');
        span.textContent = text;
        return span.innerHTML;
    } //-- end: htmlEncode ------------------------------------------------------------------------

}]); //== end: ntaAlert service definition ========================================================
